import { Component, NO_ERRORS_SCHEMA, OnDestroy, OnInit, TemplateRef, ViewChild, ViewContainerRef } from '@angular/core';
import { AppEnities, CasiCloseOptions, FormModes } from '../../common/constants.lib';
import { CommonModule } from '@angular/common';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { CheckBoxModule, InputsModule } from '@progress/kendo-angular-inputs';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { DialogCloseResult, DialogService, DialogsModule, KENDO_DIALOG, KENDO_DIALOGS, WindowCloseResult, WindowModule, WindowRef, WindowService } from '@progress/kendo-angular-dialog';
import { NotificationModule, NotificationService } from '@progress/kendo-angular-notification';
import { KENDO_PAGER, PagerModule } from '@progress/kendo-angular-pager';
import { GridModule, PageChangeEvent, PagerPosition, PagerType } from '@progress/kendo-angular-grid';
import { MulticheckFilterComponent } from '../../filters/multicheck-filter/multicheck-filter.component';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { LoggerService } from '../../shared/logger.service';
import { MeService } from '../../auth/me.service';
import { ToastrService } from 'ngx-toastr';
import { faAddressCard, faBan, faEdit, faPlusCircle, faTrash } from '@fortawesome/free-solid-svg-icons';
import { setTitle } from '../../common/utils.lib';
import { ErrorDialogModalComponent } from '../../common-components/error-dialog-modal/error-dialog-modal.component';
import { Organisation, OrganisationNew, OrganisationType } from '../../models/models.lib';
import { CompositeFilterDescriptor, distinct, filterBy, orderBy, SortDescriptor } from '@progress/kendo-data-query';
import { OrganisationService } from '../organisation.service';
import { OrganisationEditComponent } from '../organisation-edit.component/organisation-edit.component';
import {
  OrganisationDisciplinesComponent
} from '../organisation-disciplines.component/organisation-disciplines.component';
import { OrganisationUsersComponent } from '../organisation-users.component/organisation-users.component';
import { ApiService } from '../../api/api.service';
import { OrganisationDisciplinesWin } from '../organisation-disciplines-win/organisation-disciplines-win';
import { ModalWindowService } from '../../shared/modal-window.service';
import { OrganisationEditWin } from '../organisation-edit-win/organisation-edit-win';
import { OrganisationUsersWin } from '../organisation-users-win/organisation-users-win';

const { ORGANISATIONS } = AppEnities;

@Component({
  selector: 'casi-organisation-list.component',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule, ReactiveFormsModule,
    FontAwesomeModule,
    InputsModule,
    ButtonsModule,
    DialogsModule,
    WindowModule,
    NotificationModule,
    CheckBoxModule,
    KENDO_PAGER,
    KENDO_DIALOGS,
    PagerModule,
    GridModule,
    MulticheckFilterComponent
  ],
  schemas: [NO_ERRORS_SCHEMA],
  templateUrl: './organisation-list.component.html',
  styleUrl: './organisation-list.component.scss'
})
export class OrganisationListComponent implements OnInit, OnDestroy {

  public view: Observable<Organisation[]> = new Observable<Organisation[]>();
  public rawData: Organisation[] = [];
  public filter: CompositeFilterDescriptor = { logic: "and", filters: [] };
  public gridData: Organisation[] = filterBy(this.rawData, this.filter);

  public currentItem: OrganisationType = OrganisationNew.create();
  public currentOrg!: Organisation;

  public pagerTypes = ["numeric", "input"];
  public type: PagerType = "numeric";
  public info = true;
  public pageSizes = true;
  public previousNext = true;
  public position: PagerPosition = "bottom";

  public pageSize = 10;

  public formMode: FormModes = FormModes.NOTSET;

  public canUpdate: boolean = true;
  public canCreate: boolean = true;
  public canView: boolean = true;
  public isSuperUser: boolean = false;

  public editWinOpen: Boolean = false;

  private _showTitle = new BehaviorSubject<string>('');
  get showTitle() {
    return this._showTitle.getValue();
  }
  set showTitle(val: string) {
    this._showTitle.next(val);
  }

  @ViewChild("appendTo", { read: ViewContainerRef }) appendTo: ViewContainerRef | undefined;
  @ViewChild("container", { read: ViewContainerRef })
  public containerRef: ViewContainerRef | undefined;

  private _windowRef: WindowRef | undefined;

  private _subs: Subscription[] = [];

  constructor(
    private logger: LoggerService,
    private notificationService: NotificationService,
    private me: MeService,
    public dataService: OrganisationService,
    private apiService: ApiService,
    public faIcons: FaIconLibrary,
    private dialogService: DialogService,
    private windowService: WindowService,
    private toastr: ToastrService,
    private modalWindowService: ModalWindowService
  ) {
    faIcons.addIcons(
      faPlusCircle, faAddressCard, faEdit,
      faTrash,
      faBan
    );
  }

  public sort: SortDescriptor[] = [
    { field: "organisation_name", dir: "asc" }
  ];

  //#region Ng event handlers

  ngOnInit(): void {
    this.logger.debug('ngOnInit...');
    const { can_update, can_create, can_view } = this.me.allICanDo(ORGANISATIONS);
    this.canUpdate = can_update;
    this.canCreate = can_create;
    this.canView = can_view;
    this.isSuperUser = this.me.get()?.superuser || false;
    // if(this.me.email)
    this.dataService.query();

    this._subs.push(this.dataService.subscribe(e => {
      this.logger.debug('ngOnInit:v: ', e);
      this.rawData = e;
      this.loadData();
    }));
  }

  ngOnDestroy(): void {
    this.logger.debug('ngOnDestroy...');
    this._subs.forEach(s => s.unsubscribe());
  }

  //#endregion

  //#region Grid functions

  public pageChange(event: PageChangeEvent): void {
    this.logger.debug(`pageChange: `, event);
  }

  onClearFilters(gridName: string) {
    this.logger.debug("onClearFilters: ", gridName);
    this.filter = { logic: "and", filters: [] };
    this.loadData();
  }

  public sortChange(sort: SortDescriptor[]): void {
    this.logger.debug("sortChange: ", sort);
    this.sort = sort;
    this.loadData();
  }

  public filterChange(filter: CompositeFilterDescriptor): void {
    this.logger.debug('filterChange: filter: ', filter);
    this.filter = filter;
    this.loadData();
  }

  public distinctPrimitive(fieldName: string): any[] {
    this.logger.debug('distinctPrimitive: fieldName: ', fieldName);
    return distinct(this.gridData, fieldName).map((item: any) => item[fieldName]);
  }

  //#endregion

  //#region Public functions

  setActive(dataItem: any): void {
    dataItem.active = !dataItem.active;
    this.logger.debug('setActive:dataItem: ', dataItem);
    this.currentItem = Organisation.create(dataItem);
    this.saveActive(this.currentItem);
  }

  public onAddClick(titleBar: TemplateRef<unknown>): void {
    this.logger.debug("onAdd...");
    this.currentItem = OrganisationNew.create();
    this.formMode = FormModes.NEW;
    this.logger.debug("onAdd:currentItem ", this.currentItem);
    this.showTitle = setTitle(this.formMode, 'ORGANISATION');
    this.openAddEditDialog(this.formMode, this.currentItem, titleBar);
  }

  onEditClick(dataItem: any, titleBar: TemplateRef<unknown>): void {
    this.logger.debug("onEditClick: ", dataItem);
    this.currentItem = Organisation.create(dataItem);
    this.formMode = FormModes.EDIT;
    this.showTitle = setTitle(this.formMode, 'ORGANISATION');
    this.logger.debug("onEditClick:currentItem ", this.currentItem);
    this.openAddEditDialog(this.formMode, this.currentItem, titleBar);
  }

  public onDisciplineClick(dataItem: Organisation): void {
    this.logger.debug("onSubscaleClick: ", dataItem);
    let item = Organisation.create(dataItem);
    this.currentOrg = item;
    this.logger.debug("onSubscaleClick:currentItem ", item);
    this.openDisciplineDialog(item);
  }

  public onUserClick(dataItem: Organisation): void {
    this.logger.debug("onUserClick: ", dataItem);
    let item = Organisation.create(dataItem);
    this.logger.debug("onUserClick:currentItem ", item);
    this.openUserDialog(item);
  }

  public close(args: any): void {
    this.logger.debug('close...', args);
    this.editWinOpen = false;
  }

  //#endregion

  //#region Private function

  private setDisciplineWinInstanceInputs(dataItem: Organisation): void {
    if (this._windowRef) {
      const inputs = this._windowRef.content.instance;
      // inputs.title = 'MANAGE DISCIPLINE SUBSCALES';
      inputs.organisation = dataItem;
      this.logger.debug('setWinInstanceInputs: ', inputs);
    }
  }

  private openDisciplineDialog(dataItem: Organisation): void {
    this.logger.debug("openDisciplineDialog...");
    this.closeWindowRef();
    this._windowRef = this.modalWindowService.open({
      title: 'MANAGE ORGANISATION DISCIPLINES',
      content: OrganisationDisciplinesWin,
      minWidth: 320,
      width: 800,
      modal: true
    });

    this.setDisciplineWinInstanceInputs(dataItem);

    // this.editWinOpen = true;

    this._windowRef.result.subscribe((r: any) => {
      this.logger.debug('openSubscaleDialog:result:', r);
      // if (r instanceof DialogCloseResult) {
      if (r instanceof WindowCloseResult) {
        this.closeWindowRef();
        this.dataService.query();
      }
    });

    this._windowRef.content.instance.onCancel.subscribe(() => {
      this.logger.debug('openSubscaleDialog:onCancel');
      this.closeWindowRef();
    });

  }

  private openUserDialog(dataItem: Organisation): void {
    this.logger.debug("openUserDialog...");
    this.closeWindowRef();

    this._windowRef = this.modalWindowService.open({
      title: 'MANAGE ORGANISATION USERS',
      content: OrganisationUsersWin,
      minWidth: 320,
      width: 800,
    });
    // const dialogRef = this.dialogService.open({
    //   content: OrganisationUsersComponent,
    //   minWidth: 320,
    //   width: 800,
    // });

    this.setDisciplineWinInstanceInputs(dataItem);
    // const contentRef = dialogRef.content.instance as OrganisationUsersComponent;
    // contentRef.title = "MANAGE ORGANISATION USERS";
    // contentRef.organisation = dataItem;

    this._windowRef.result.subscribe((r: any) => {
      this.logger.debug('openStudentDialog:result:', r);
      // if (r instanceof DialogCloseResult) {
      if (r instanceof WindowCloseResult) {
        this.closeWindowRef();
        this.dataService.query();
      }
    });

    this._windowRef.content.instance.onClose.subscribe(() => {
      this.logger.debug('openStudentDialog:onClose');
      this.closeWindowRef();
    });

    // dialogRef.result.subscribe((r: any) => {
    //   this.logger.debug('openSubscaleDialog:result:', r);
    //   if (r instanceof DialogCloseResult) {
    //     return;
    //   }
    //   // if(this.me.email)
    //   this.dataService.query();
    // });
  }

  private setAddEditWinInstanceInputs(dataItem: OrganisationType, mode: FormModes): void {
    if (this._windowRef) {
      const inputs = this._windowRef.content.instance;
      inputs.title = setTitle(mode, 'ORGANISATION');
      inputs.organisation = dataItem;
      inputs.mode = mode;
      inputs.existingOrganisations = this.rawData;
      this.logger.debug('setAddEditWinInstanceInputs: ', inputs);
    }
  }

  private openAddEditDialog(mode: FormModes, dataItem: OrganisationType, titleBar: TemplateRef<unknown>): void {
    this.logger.debug("openAddEditDialog...");
    this.closeWindowRef();

    this._windowRef = this.modalWindowService.open({
      titleBarContent: titleBar,
      content: OrganisationEditWin,
      minWidth: 320,
      width: 1200,
    });

    // const dialogRef = this.dialogService.open({
    //   content: OrganisationEditComponent,
    //   minWidth: 320,
    //   width: 1200,
    // });

    this.setAddEditWinInstanceInputs(dataItem, mode);

    // const contentRef = dialogRef.content.instance as OrganisationEditComponent;
    // contentRef.title = setTitle(mode, 'ORGANISATION');
    // contentRef.organisation = dataItem;
    // contentRef.mode = mode;
    // contentRef.existingOrganisations = this.rawData;

    this._windowRef.content.instance.onSubmit.subscribe((formData: FormGroup) => {
      this.logger.debug('openAddEditDialog:formData: ', formData);
      this.handleClose(formData.value, mode);
    });

    this._windowRef.result.subscribe((r: any) => {
      this.logger.debug('openAddEditDialog:result:', r);
      // if (r instanceof DialogCloseResult) {
      if (r instanceof WindowCloseResult) {
        this.closeWindowRef();
        // this.dataService.query();
      }
    });

    this._windowRef.content.instance.onClose.subscribe(() => {
      this.logger.debug('openAddEditDialog:onClose');
      this.closeWindowRef();
    });

    // dialogRef.result.subscribe((r: any) => {
    //   this.logger.debug('openAddEditDialog:result:', r);
    //   if (r instanceof DialogCloseResult) {
    //     return;
    //   }
    //   if (r.text === CasiCloseOptions.Saved) {
    //     this.logger.debug("openAddEditDialog:saved... ", contentRef.editForm.value);
    //     this.handleClose(contentRef.editForm.value, mode);
    //   }
    // });
  }

  private handleClose(data: any, mode: FormModes): void {
    this.logger.debug('handleClose...', data, mode);
    this.save(data, mode);
  }

  private save(organisation: any, mode: FormModes): void {
    this.logger.debug('saveDiscipline...', organisation, mode);
    if (mode === FormModes.EDIT) {
      this.dataService.update(organisation.id, organisation)
        .then(resp => {
          this.logger.debug('saveDiscipline:update:resp: ', resp);
          this.notify("Organisation Updated");
          this.closeWindowRef();
          // if(this.me.email)
          this.dataService.query();
        })
        .catch(err => {
          this.logger.error('saveDiscipline:update:err: ', err);
          this.notify("Error Updating Organisation", true);
        });
    } else {
      this.dataService.add(organisation)
        .then(resp => {
          this.logger.debug('saveDiscipline:add:resp: ', resp);
          this.notify("Organisation Added");
          this.closeWindowRef();
          // if(this.me.email)
          this.dataService.query();
        })
        .catch(err => {
          this.logger.error('saveDiscipline:add:err: ', err);
          this.notify("Error Creating Organisation", true);
        });
    }
  }

  private saveActive(organisation: any): void {
    this.logger.debug('saveDiscipline...', organisation);
    this.dataService.update(organisation.id, organisation)
      .then(resp => {
        this.logger.debug('saveDiscipline:update:resp: ', resp);
        this.notify(organisation.active ? "Organisation Activated" : "Organisation Deactivated");
        this.closeWindowRef();
        // if (this.me.email)
        this.dataService.query();
      })
      .catch(err => {
        this.logger.error('saveDiscipline:update:err: ', err);
        this.notify("Error Updating Organisation", true);
      });
  }

  private notify(msg: string, error: boolean = false): void {
    this.logger.debug("notify", msg);
    if (error)
      this.toastr.error(msg);
    else
      this.toastr.success(msg);
  }

  private closeWindowRef(): void {
    this.logger.debug(`closeWindowRef...`);
    if (this._windowRef) {
      this.modalWindowService.close(this._windowRef);
      this._windowRef = undefined;
    }
  }

  private loadData(): void {
    this.gridData = orderBy(filterBy(this.rawData, this.filter), this.sort);
    this.pageSize = this.gridData.length;
  }

  //#endregion

}
